import React, { useState, useEffect, useCallback } from 'react';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { CRow, CCol, CBadge } from '@coreui/react';
import CLabel from '../../../../../migration/CLabel';
import { callTokenApi } from '../../../../../../apiCaller';
import { formatDateTimeZone, parseJson, toastError } from '../../../../../../utils';
import { ADMIN_CENTRALIZED_PERSON_RETRIEVE, DEFAULT_DATE_TIME_FORMAT, LLM_STATUS } from '../../../../../../constants';
import PersonIdentifiersPanel from '../tabs/PersonIdentifiersPanel';
import { enrichType } from '../components/Ultils';

const renderBoolean = (value, trueText = "Yes", falseText = "No", undefinedText = "none") => {
    if (typeof value === 'boolean') {
        return value ? <CBadge color="success" shape="rounded-pill">{trueText}</CBadge> : <CBadge color="danger" shape="rounded-pill">{falseText}</CBadge>;
    }
    return <CBadge color="secondary" shape="rounded-pill">{undefinedText}</CBadge>;
};

const renderJsonData = (jsonData, fieldName = "Data") => {
    if (jsonData === null || jsonData === undefined) return <small className="text-muted">none</small>;
    if (typeof jsonData === 'object') {
        if (Array.isArray(jsonData) && jsonData.length === 0) return <small className="text-muted">Empty</small>;
        return (
            <details className="json-details mb-2">
                <summary style={{ cursor: 'pointer', fontWeight: '500' }}>View {fieldName} (JSON)</summary>
                <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    background: '#f8f9fa',
                    padding: '10px',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    {JSON.stringify(jsonData, null, 2)}
                </pre>
            </details>
        );
    }
    return String(jsonData);
};

const renderOtherRole = (data, fieldName = "Data") => {
    const otherRoles = data && parseJson(data);

    if (!otherRoles || otherRoles.length === 0) {
        return <small className="text-muted">none</small>;
    }

    return (
        <details className="json-details mb-2">
            <summary style={{ cursor: 'pointer', fontWeight: '500' }}>View {fieldName} (JSON)</summary>
            <pre style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                background: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto'
            }}>
                {JSON.stringify(otherRoles, null, 2)}
            </pre>
        </details>
    );
}

const CentralizedPerson = ({ attributes }) => {
    const { id: personId, timeZone } = attributes;

    const [personData, setPersonData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!personId) {
            setError("Person ID is missing.");
            setIsLoading(false);
            setPersonData(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await callTokenApi(`${ADMIN_CENTRALIZED_PERSON_RETRIEVE.replace(':id', personId)}`, 'GET', null);

            if (response.status === 200 && response.data?.success) {
                setPersonData(response.data.data);
            } else {
                const errorMsg = response.data?.message || `Failed to fetch person data (status: ${response.status})`;
                setError(errorMsg);
                toastError({ data: { message: errorMsg } });
                setPersonData(null);
            }
        } catch (err) {
            console.error("API call failed for person detail:", err);
            const errorMessage = err.message || 'An unexpected error occurred.';
            setError(errorMessage);
            toastError({ data: { message: errorMessage } });
            setPersonData(null);
        } finally {
            setIsLoading(false);
        }
    }, [personId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (isLoading) {
        return <CenterSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger" role="alert">Error: {error}</div>;
    }

    if (!personData) {
        return <div className="alert alert-info">No person data found for ID: {personId}</div>;
    }

    const { LLMstatus } = personData;

    const fieldsToDisplay = [
        // Basic Information
        { label: "First Name", value: personData.firstName },
        { label: "Last Name", value: personData.lastName },
        { label: "Current Title", value: personData.currentTitle },
        { label: "Current Company", value: personData.currentCompany },
        { label: "External ID", value: personData.externalId !== '[]' ? personData.externalId : 'none' },
        { label: "First Session", value: personData.firstSessionId },

        // Contact Information
        { label: "Primary Email", value: personData.primaryEmail, type: 'email' },
        { label: "LinkedIn URL", value: personData.linkedinURL, type: 'url' },
        { label: "Other Roles", value: renderOtherRole(personData.otherRoles, 'Other Roles') },
        { label: "Skills", value: renderJsonData(parseJson(personData.skills), 'Skills') },
        { label: "Volunteering", value: renderJsonData(parseJson(personData.volunteering), 'Volunteering') },
        { label: "Certification", value: renderJsonData(parseJson(personData.certification), 'Certification') },
        { label: "Education", value: renderJsonData(parseJson(personData.education), 'Education') },
        { label: "Projects", value: renderJsonData(parseJson(personData.projects), 'Projects') },
        { label: "Recommendations", value: renderJsonData(parseJson(personData.recommendations), 'Recommendations') },
        { label: "Address", value: personData.address },

        // Source Information
        { label: "First Source", value: personData.sourceFirst },
        { label: "Source Path", value: personData.sourcePath },
        { label: "Details", value: personData.details },

        // Status and Validation
        { label: "Valid Status", value: personData.valid, type: 'statusBadge' },
        { label: "LLM Status", value: renderBoolean(LLMstatus === LLM_STATUS.GOOD ? true : LLMstatus, LLMstatus, LLMstatus, LLMstatus), type: 'statusBadge' },
        { label: "Enrich Type", value: personData.sourcePath?.includes('revealed') ? enrichType(personData) : null },
        { label: "Valid LinkedIn Status", value: personData.validLinkedin },
        { label: "Score", value: personData.score },

        // Boolean Flags
        { label: "Verified", value: renderBoolean(personData.verified) },
        { label: "Enriched", value: renderBoolean(personData.enriched) },
        { label: "LLM Run", value: personData.LLMstatus },
        { label: "Valid Company Match", value: renderBoolean(personData.validCompanyMatch) },
        { label: "Invalid Company Match", value: renderBoolean(personData.invalidCompanyMatch) },
        { label: "Has Multiple Companies", value: renderBoolean(personData.hasMultipleCompanies) },

        // Email Properties
        { label: "Email Type", value: personData.emailType },
        { label: "Is Role Email", value: renderBoolean(personData.isRoleEmail) },
        { label: "Is Catch-all Email", value: renderBoolean(personData.isCatchallEmail) },
        { label: "Is Disposable Email", value: renderBoolean(personData.isDisposableEmail) },

        // JSON Data
        { label: "Emails", value: renderJsonData(personData.emails, "Emails") },
        { label: "Emails (LLM)", value: renderJsonData(personData.emailsLLM, "LLM Emails") },
        { label: "Validated Emails", value: renderJsonData(personData.validatedEmails, "Validated Emails") },
        { label: "Phones", value: renderJsonData(personData.phones, "Phones") },
        { label: "Emails Current Role", value: renderJsonData(personData.emailsCurrentRole, "Emails Current Role") },

        { label: "Created At", value: formatDateTimeZone(personData.created_at, DEFAULT_DATE_TIME_FORMAT, timeZone) },
        { label: "Updated At", value: formatDateTimeZone(personData.updated_at, DEFAULT_DATE_TIME_FORMAT, timeZone) }
    ];

    const renderFieldValue = (field) => {
        if (field.type === 'email' && field.value) {
            return <a href={`mailto:${field.value}`}>{field.value}</a>;
        }
        if (field.type === 'url' && field.value) {
            return <a href={field.value} target="_blank" rel="noopener noreferrer">{field.value}</a>;
        }
        return field.value ?? <small className="text-muted">none</small>;
    };

    return (
        <div className="rule-detail">
            <h3 className='mb-4'>
                Centralized Person Details: {personData.firstName || ''} {personData.lastName || ''}
            </h3>

            <CRow>
                {fieldsToDisplay.map(field => (
                    <React.Fragment key={field.label}>
                        <CCol md="3" className="mb-2">
                            <CLabel className="fw-semibold">{field.label}:</CLabel>
                        </CCol>

                        <CCol md="9" className="mb-2">
                            {renderFieldValue(field)}
                        </CCol>
                    </React.Fragment>
                ))}

                <CCol md="12">
                    <PersonIdentifiersPanel
                        account={{ id: personData.accountId, timeZone }}
                        personId={personData.id}
                        title='Person Identifiers'
                        showLv2={true}
                    />
                </CCol>
            </CRow>
        </div>
    );
};

export default CentralizedPerson;